import { useTranslation } from 'react-i18next'
import { Columns3, RotateCcw } from 'lucide-react'
import { type Column, type Table } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/** Human readable name for a column, for menus and pickers. */
export function columnLabel<TData>(column: Column<TData, unknown>): string {
  const { meta, header } = column.columnDef
  if (meta?.label) return meta.label
  if (typeof header === 'string' && header) return header
  return column.id
}

interface ColumnPickerProps<TData> {
  table: Table<TData>
  onReset: () => void
  /** The list's compact view, offered as a toggle under the columns. */
  compact?: boolean
  onCompactChange?: (compact: boolean) => void
}

/**
 * Dropdown that toggles table columns on and off — the columns marked
 * `meta.optional` are the extra object fields hidden by default.
 */
export function ColumnPicker<TData>({ table, onReset, compact, onCompactChange }: ColumnPickerProps<TData>) {
  const { t } = useTranslation()

  const hideable = table.getAllLeafColumns().filter((c) => c.getCanHide())
  if (!hideable.length) return null

  // Columns naming a section are listed under it rather than with the rest.
  const sectioned = hideable.filter((c) => !!c.columnDef.meta?.section)
  const rest = hideable.filter((c) => !c.columnDef.meta?.section)
  const standard = rest.filter((c) => !c.columnDef.meta?.optional)
  const optional = rest.filter((c) => c.columnDef.meta?.optional)
  const extraCount = [...optional, ...sectioned].filter((c) => c.getIsVisible()).length

  const sections = [...new Set(sectioned.map((c) => c.columnDef.meta!.section!))].map(
    (section) => ({
      section,
      columns: sectioned.filter((c) => c.columnDef.meta?.section === section),
    })
  )

  const item = (column: Column<TData, unknown>) => (
    <DropdownMenuCheckboxItem
      key={column.id}
      checked={column.getIsVisible()}
      onCheckedChange={(value) => column.toggleVisibility(!!value)}
      onSelect={(e) => e.preventDefault()}
    >
      {columnLabel(column)}
    </DropdownMenuCheckboxItem>
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          <Columns3 className="size-4" />
          {t('common.columns')}
          {extraCount > 0 && (
            <span className="text-(--ad-text-muted)">+{extraCount}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-96 w-56 overflow-y-auto">
        <DropdownMenuLabel>{t('common.columns')}</DropdownMenuLabel>
        {standard.map(item)}
        {optional.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t('common.additionalColumns')}</DropdownMenuLabel>
            {optional.map(item)}
          </>
        )}
        {sections.map((group) => (
          <div key={group.section}>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{group.section}</DropdownMenuLabel>
            {group.columns.map(item)}
          </div>
        ))}
        <DropdownMenuSeparator />
        {onCompactChange && (
          <DropdownMenuCheckboxItem
            checked={!!compact}
            onCheckedChange={(value) => onCompactChange(!!value)}
            onSelect={(e) => e.preventDefault()}
          >
            {t('common.compactView')}
          </DropdownMenuCheckboxItem>
        )}
        <DropdownMenuItem onSelect={onReset}>
          <RotateCcw className="size-4" />
          {t('common.resetColumns')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
